import { Button, Typography, IconButton, Alert, AlertTitle, useMediaQuery, useTheme } from "@mui/material";
import { Close as CloseIcon, LocalOffer as LocalOfferIcon } from "@mui/icons-material";
import React, { useContext, useEffect, useState } from "react";
import { ReactElement } from "react";
import { PlanStatus, PlanType } from "../api/models/User";
import { useAlert } from "../contexts/alert/useAlert";
import { UserContext } from "../contexts/UserContext";
import { Action, Category } from "../analytics/Tracking";
import { SubscribeDialog } from "./SubscribeDialog";
import { useTracking } from "../analytics/useTracking";
import { useConfig } from "../hooks/useConfig";


export interface SubscriptionRibbonProps {
  sessionId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function SubscriptionRibbon(_props: SubscriptionRibbonProps): ReactElement {

  const alert = useAlert();
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isBreakpointGreaterThanXs = useMediaQuery(theme.breakpoints.up('sm'));
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [ribbonMessage, setRibbonMessage] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isManuallyClosed, setIsManuallyClosed] = useState<boolean>(false);
  const { track } = useTracking();
  const config = useConfig();
  const discountPercent = config.INTRODUCTORY_DISCOUNT_PERCENT;
  const discountMonths = config.INTRODUCTORY_DISCOUNT_MONTHS;
  const hasDiscount = discountPercent > 0;
  const discountLabel = hasDiscount ? `${discountPercent}% off your first ${discountMonths} months` : '';

  useEffect(() => {
    // Check to see if this is a redirect back from Checkout
    const query = new URLSearchParams(window.location.search);
    if (query.get("success")) {
      //TODO pri2: tracking for all callbacks are not working because GA isn't initialized. UseEffect of childcomponents is called before parents so the hole way of tracking has to be refactored. maybe with a shared context and hooks. but GA Universal is on its way out so maybe it has to go together with thinking about the future of event tracking in generel.
      track.event(Category.User, Action.PurchaseCompleted, user?.idPId);
      alert.showMessage("Thank you for your purchase, it means a lot to us 🙏", "success");
    }

    if (query.get("canceled")) {
      track.event(Category.User, Action.PurchaseCanceled, user?.idPId);
      alert.showMessage("your purhcase is canceled, please contact us if you have any questions", "warning");
    }
  }, []);

  useEffect(() => {
    // Check if ribbon was manually closed in this session
    const closedState = sessionStorage.getItem('subscriptionRibbonClosed');
    if (closedState === 'true') {
      setIsManuallyClosed(true);
      return;
    }

    let visible = false;
    if (user) {
      const plan = user.plan;
      const isPremiumCanceled = plan.type == PlanType.Premium && plan.status == PlanStatus.Canceled;
      const isTrial = plan.type == PlanType.Trial;
      let message = "";
        if (isTrial) {
          message =
            user.plan.daysLeft > 0
              ? hasDiscount
                ? `Upgrade now and get ${discountLabel}. Unlimited players, moderator insights, and more — locked in at the discounted rate.`
                : `Only ${user.plan.daysLeft} ${user.plan.daysLeft === 1 ? "day" : "days"} left! Unlock unlimited players, advanced insights, and keep your moderator powers.`
              : hasDiscount
                ? `Subscribe today to reactivate your account and claim ${discountLabel}.`
                : `Your trial has expired. Upgrade to premium to reactivate your account and regain access to all features.`;
          visible = true;
        if (user.plan.daysLeft <= 0) {
          track.event(Category.User, Action.TrialEnded, user.idPId);

        }
      } else if (isPremiumCanceled) {
        message = "You subscription has been canceled and your account is deactivated. If you choose to pay for the subscription your account will be reactivated.";
        visible = true;
        // Automatically open the subscription dialog when premium subscription is canceled
        setIsDialogOpen(true);
      }
      setRibbonMessage(message);
    }
    setIsVisible(visible);
  }, [user]);

  const handleClose = () => {
    setIsManuallyClosed(true);
    setIsVisible(false);
    sessionStorage.setItem('subscriptionRibbonClosed', 'true');
  };

  return isVisible && !isManuallyClosed ? (
    <>
      <Alert 
        severity="error"
        icon={<LocalOfferIcon fontSize="medium" />}
        action={
          <>
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setIsDialogOpen(true)}
              sx={{ 
                mr: 1,
                ...(!isBreakpointGreaterThanXs && { 
                  px: 3,
                  minWidth: 'auto'
                })
              }}
              variant="contained"
            >
              {hasDiscount ? `Claim ${discountPercent}% Off` : 'Subscribe Now'}
            </Button>
            {!(user?.plan.type === PlanType.Trial && user.plan.daysLeft <= 0) && (
              <IconButton 
                size="small" 
                color="inherit" 
                onClick={handleClose} 
                aria-label="Close ribbon"
              >
                <CloseIcon />
              </IconButton>
            )}
          </>
        }
      >
        <AlertTitle>
          {user?.plan.type === PlanType.Trial && user.plan.daysLeft > 0
            ? hasDiscount
              ? `${discountPercent}% Off — ${user.plan.daysLeft} ${user.plan.daysLeft === 1 ? 'Day' : 'Days'} Left on Your Trial`
              : `Trial Expires in ${user.plan.daysLeft} ${user.plan.daysLeft === 1 ? 'Day' : 'Days'}`
            : user?.plan.type === PlanType.Trial && user.plan.daysLeft <= 0
            ? hasDiscount
              ? isBreakpointGreaterThanXs ? `Trial Expired — ${discountPercent}% Off Your First ${discountMonths} Months` : `${discountPercent}% Off Available`
              : isBreakpointGreaterThanXs ? 'Trial Expired - Upgrade to Continue' : 'Trial Expired'
            : isBreakpointGreaterThanXs
            ? 'Subscription Canceled'
            : 'Canceled'
          }
        </AlertTitle>
        {isBreakpointGreaterThanXs && (
          <Typography variant="body2">
            {ribbonMessage}
          </Typography>
        )}
      </Alert>
      {isDialogOpen && <SubscribeDialog open={isDialogOpen} onClosed={() => setIsDialogOpen(false)} />}
    </>
  ) : (
    <></>
  );
}
